import { useEffect, useMemo, useState } from "react";
import { UploadCloud, Send, PackageOpen, Loader2, ExternalLink, Download, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const MB_PER_CLIP = 30;
const Radio = ({ value, label, hint, t }) => (
  <label className="flex items-start gap-2 text-sm cursor-pointer">
    <RadioGroupItem value={value} data-testid={t} className="mt-0.5" />
    <span>{label}{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}</span>
  </label>
);

export const useTransferPoll = (id, onDone) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    const tick = async () => {
      try {
        const { data } = await http.get(`/transfers/${id}`);
        if (!live) return;
        setT(data);
        if (data.status === "running") setTimeout(tick, 2500);
        else onDone?.(data);
      } catch (e) { if (live) setT({ status: "failed", error: errMsg(e) }); }
    };
    tick();
    return () => { live = false; };
  }, [id]); // eslint-disable-line
  return t;
};

const Status = ({ t, children }) => !t ? null : (
  <div className="text-xs rounded-md border border-[#2A3245] p-3 space-y-2" data-testid="transfer-status">
    {t.status === "running" && <div className="flex items-center gap-2 text-violet-300"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t.progress || "Working"}…</div>}
    {t.status === "failed" && <div className="flex items-center gap-2 text-red-400" data-testid="transfer-error"><AlertCircle className="w-3.5 h-3.5" />{t.error}</div>}
    {t.status === "done" && children}
  </div>
);

const SceneChooser = ({ scenes, mode, setMode, sel, setSel, takes, setTakes }) => {
  const chosen = mode === "all" ? scenes : scenes.filter((s) => sel.includes(s.id));
  const clips = chosen.reduce((n, s) => n + (takes === "all" ? (s.takes_list || []).length : s.video_path ? 1 : 0), 0);
  return (
    <div className="space-y-3">
      <RadioGroup value={mode} onValueChange={setMode} className="space-y-1.5">
        <Radio value="all" label={`Whole episode (${scenes.length} scene${scenes.length === 1 ? "" : "s"})`} t="move-scenes-all" />
        <Radio value="some" label="Only the scenes I tick" t="move-scenes-some" />
      </RadioGroup>
      {mode === "some" && (
        <div className="max-h-40 overflow-y-auto rounded border border-[#2A3245] p-2 space-y-1" data-testid="move-scene-list">
          {scenes.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox data-testid={`move-scene-${s.number}`} checked={sel.includes(s.id)} onCheckedChange={(c) => setSel(c ? [...sel, s.id] : sel.filter((x) => x !== s.id))} />
              <span className="font-mono text-amber-400">SC {String(s.number).padStart(3, "0")}</span>
              <span className="truncate text-slate-300">{s.heading}</span>
              <span className="ml-auto text-slate-500">{s.status}</span>
            </label>
          ))}
        </div>
      )}
      <RadioGroup value={takes} onValueChange={setTakes} className="space-y-1.5">
        <Radio value="current" label="Only the chosen take of each scene" hint="Approved / current take — much smaller" t="move-takes-current" />
        <Radio value="all" label="All takes" hint="Keeps every take so you can still switch on live" t="move-takes-all" />
      </RadioGroup>
      <div className="font-mono text-[11px] text-slate-500" data-testid="move-size-estimate">
        {chosen.length} scene{chosen.length === 1 ? "" : "s"} · {clips} clip{clips === 1 ? "" : "s"} · about {clips * MB_PER_CLIP} MB
      </div>
    </div>
  );
};

const Target = ({ episodes, target, setTarget, mode, setMode }) => (
  <div className="space-y-2">
    <select data-testid="move-target-select" value={target} onChange={(e) => setTarget(e.target.value)}
      className="w-full h-9 rounded-md bg-[#0B0D14] border border-[#2A3245] px-2 text-sm">
      <option value="new">As a new episode</option>
      {episodes.map((e) => <option key={e.id} value={e.id}>Into “{e.title}” ({e.scene_count} scene{e.scene_count === 1 ? "" : "s"})</option>)}
    </select>
    {target !== "new" && (
      <RadioGroup value={mode} onValueChange={setMode} className="space-y-1.5">
        <Radio value="append" label="Add as new scenes after the existing ones" t="move-mode-append" />
        <Radio value="replace" label="Replace scenes with the same number" hint="Replaced scenes go to Recently deleted for 10 minutes" t="move-mode-replace" />
      </RadioGroup>
    )}
  </div>
);

const SendDirect = ({ ep, payload, disabled }) => {
  const { user } = useAuth();
  const [url, setUrl] = useState(localStorage.getItem("live_url") || "https://studio.brimi.in");
  const [email, setEmail] = useState(user?.email || "");
  const [pw, setPw] = useState("");
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("new");
  const [mode, setMode] = useState("append");
  const [jid, setJid] = useState(null);
  const t = useTransferPoll(jid, (d) => d.status === "done" && toast.success("Sent to live"));
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/live/connect", { url, email, password: pw });
      localStorage.setItem("live_url", url); setConn(data); setPw("");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const send = async () => {
    if (!window.confirm(`Send to ${url}? Nothing is regenerated and no credits are used.`)) return;
    try { const { data } = await http.post(`/episodes/${ep.id}/send-live`, { ...payload, token: conn.token, target, update_mode: mode }); setJid(data.transfer_id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!conn) {
    return (
      <div className="space-y-2">
        <Input data-testid="live-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://studio.brimi.in" />
        <Input data-testid="live-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Live admin email" />
        <Input data-testid="live-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Live admin password" />
        <p className="text-[11px] text-slate-500">Your live password is used once to sign in and is never saved.</p>
        <Button data-testid="live-connect-btn" disabled={busy || !pw || !email} onClick={connect} className="w-full rounded-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}Connect to live studio
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="live-connected"><CheckCircle2 className="w-3.5 h-3.5" />Connected to {url}</div>
      <Target episodes={conn.episodes} target={target} setTarget={setTarget} mode={mode} setMode={setMode} />
      <Button data-testid="send-live-btn" disabled={disabled || t?.status === "running"} onClick={send} className="w-full rounded-full">
        <Send className="w-4 h-4 mr-2" />Send to live
      </Button>
      <Status t={t}>
        <div className="text-emerald-300" data-testid="send-result">
          Done — {t?.result?.scenes_added} added, {t?.result?.scenes_replaced} replaced, {t?.result?.characters_added} new cast.
          <a href={t?.result?.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-cyan-300 hover:underline" data-testid="open-on-live-link">
            Open on live <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </Status>
    </div>
  );
};

const PackageDownload = ({ ep, payload, disabled }) => {
  const [jid, setJid] = useState(null);
  const t = useTransferPoll(jid);
  const build = async () => {
    try { const { data } = await http.post(`/episodes/${ep.id}/package`, payload); setJid(data.transfer_id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-500">Makes one .zip file. On studio.brimi.in use <b>Import episode</b> on the episodes page to upload it. Best for a few scenes — big files are slow on a phone.</p>
      <Button data-testid="build-package-btn" variant="secondary" disabled={disabled || t?.status === "running"} onClick={build} className="w-full rounded-full">
        <PackageOpen className="w-4 h-4 mr-2" />Build package
      </Button>
      <Status t={t}>
        <a data-testid="download-package-btn" href={`${API}/transfers/${jid}/download`}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-amber-500 text-black text-sm font-medium hover:bg-amber-400 transition-colors">
          <Download className="w-4 h-4" />Download package ({Math.round((t?.size || 0) / 1e6)} MB)
        </a>
      </Status>
    </div>
  );
};

export const MoveToLive = ({ ep, scenes }) => {
  const [mode, setMode] = useState("all");
  const [sel, setSel] = useState([]);
  const [takes, setTakes] = useState("current");
  const payload = useMemo(() => ({ scene_ids: mode === "all" ? null : sel, takes }), [mode, sel, takes]);
  const disabled = !scenes.length || (mode === "some" && !sel.length);
  return (
    <Dialog>
      <DialogTrigger data-testid="move-to-live-btn" className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-[#212738] hover:bg-[#2A3245] text-xs transition-colors" title="Copy this episode to your live studio">
        <UploadCloud className="w-3.5 h-3.5" /><span className="hidden sm:inline">Move to live</span>
      </DialogTrigger>
      <DialogContent className="bg-[#11141E] border-[#2A3245] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="move-to-live-dialog">
        <DialogHeader><DialogTitle>Move “{ep.title}” to live</DialogTitle></DialogHeader>
        <SceneChooser scenes={scenes} mode={mode} setMode={setMode} sel={sel} setSel={setSel} takes={takes} setTakes={setTakes} />
        <Tabs defaultValue="send" className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="send" data-testid="move-tab-send" className="flex-1">Send directly</TabsTrigger>
            <TabsTrigger value="package" data-testid="move-tab-package" className="flex-1">Download package</TabsTrigger>
          </TabsList>
          <TabsContent value="send" className="pt-3"><SendDirect ep={ep} payload={payload} disabled={disabled} /></TabsContent>
          <TabsContent value="package" className="pt-3"><PackageDownload ep={ep} payload={payload} disabled={disabled} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export const ImportPackage = ({ episodes, onDone }) => {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState("new");
  const [mode, setMode] = useState("append");
  const [pct, setPct] = useState(null);
  const [result, setResult] = useState(null);
  const go = async () => {
    const fd = new FormData();
    fd.append("file", file); fd.append("target", target); fd.append("update_mode", mode);
    setPct(0); setResult(null);
    try {
      const { data } = await http.post("/import/package", fd, { timeout: 0, onUploadProgress: (e) => setPct(e.total ? Math.round((e.loaded / e.total) * 100) : null) });
      setResult(data); toast.success("Episode imported"); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setPct(null);
  };
  return (
    <Dialog>
      <DialogTrigger data-testid="import-episode-btn" className="inline-flex items-center gap-2 rounded-full h-11 px-5 bg-[#212738] hover:bg-[#2A3245] text-sm transition-colors">
        <PackageOpen className="w-4 h-4" />Import episode
      </DialogTrigger>
      <DialogContent className="bg-[#11141E] border-[#2A3245] max-w-md" data-testid="import-episode-dialog">
        <DialogHeader><DialogTitle>Import an episode package</DialogTitle></DialogHeader>
        <input data-testid="import-file-input" type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <Target episodes={episodes} target={target} setTarget={setTarget} mode={mode} setMode={setMode} />
        <Button data-testid="import-submit-btn" disabled={!file || pct !== null} onClick={go} className="w-full rounded-full">
          {pct !== null ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{pct < 100 ? `Uploading ${pct}%` : "Saving scenes…"}</> : <><UploadCloud className="w-4 h-4 mr-2" />Import</>}
        </Button>
        {result && (
          <div className="text-xs text-emerald-300" data-testid="import-result">
            Imported into “{result.title}” — {result.scenes_added} added, {result.scenes_replaced} replaced, {result.characters_added} new cast.
            <a href={`/episode/${result.episode_id}`} className="ml-2 text-cyan-300 hover:underline" data-testid="open-imported-link">Open</a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
